import { ArrowLeft, CheckCircle2, MapPin, Settings2, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import PrimaryButton from '../../ui/PrimaryButton';
import SearchBar from '../../ui/searchBar';
import AmenityDetailSheet from './AmenityDetailSheet';
import OnboardingStepper from './OnboardingStepper';
import {
  AMENITY_CATEGORY_IDS,
  AMENITY_CATEGORY_META,
  AMENITY_TEMPLATES,
  templatesForCategory,
} from './communityAmenityTemplates';
import { submitFullOnboarding } from './communityOnboardingApi';
import {
  AMENITY_CATEGORIES,
  buildAmenityLocationOptions,
  siteTypeTitle,
  totalFlatsForSite,
} from './communitySiteUtils';

const DEFAULT_CLUB_HOUSE = { name: 'Club House', floors: 2 };

const CommunityAmenities = ({ siteData, siteName, onBack, onComplete, isSidebar = true }) => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [configured, setConfigured] = useState({});
  const [clubHouse, setClubHouse] = useState(DEFAULT_CLUB_HOUSE);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const [configuringId, setConfiguringId] = useState(null);
  const [draftAmenity, setDraftAmenity] = useState(null);
  const [amenitySearch, setAmenitySearch] = useState('');

  const normalizedAmenitySearch = amenitySearch.trim().toLowerCase();

  const filterTemplatesBySearch = templates => {
    if (!normalizedAmenitySearch) return templates;
    return templates.filter(template => {
      const categoryTitle =
        AMENITY_CATEGORY_META[template.category]?.title?.toLowerCase() || '';
      return (
        template.name.toLowerCase().includes(normalizedAmenitySearch) ||
        categoryTitle.includes(normalizedAmenitySearch)
      );
    });
  };

  const amenitySearchSuggestions = useMemo(() => {
    if (!normalizedAmenitySearch) return [];
    const seen = new Set();
    return AMENITY_TEMPLATES.filter(template => {
      const categoryTitle =
        AMENITY_CATEGORY_META[template.category]?.title?.toLowerCase() || '';
      const matches =
        template.name.toLowerCase().includes(normalizedAmenitySearch) ||
        categoryTitle.includes(normalizedAmenitySearch);
      if (!matches || seen.has(template.name)) return false;
      seen.add(template.name);
      return true;
    })
      .map(template => template.name)
      .slice(0, 8);
  }, [normalizedAmenitySearch]);

  const hasAmenitySearchResults = useMemo(() => {
    if (!normalizedAmenitySearch) return true;
    return AMENITY_CATEGORY_IDS.some(
      categoryId => filterTemplatesBySearch(templatesForCategory(categoryId)).length > 0
    );
  }, [normalizedAmenitySearch]);

  const locationOptions = useMemo(
    () => buildAmenityLocationOptions(siteData.blocks, clubHouse),
    [siteData.blocks, clubHouse]
  );

  const getTemplate = id => AMENITY_TEMPLATES.find(t => t.id === id);

  const closeAmenitySheet = () => {
    setConfiguringId(null);
    setDraftAmenity(null);
  };

  const saveAmenityConfig = (id, updated) => {
    setConfigured(prev => ({ ...prev, [id]: updated }));
    setSelectedIds(prev => new Set([...prev, id]));
    closeAmenitySheet();
  };

  const openAmenitySheet = id => {
    if (!configured[id]) return;
    setDraftAmenity(null);
    setConfiguringId(id);
  };

  const toggleAmenity = id => {
    if (selectedIds.has(id)) {
      const nextIds = new Set(selectedIds);
      nextIds.delete(id);
      setSelectedIds(nextIds);
      const nextConfigured = { ...configured };
      delete nextConfigured[id];
      setConfigured(nextConfigured);
      if (configuringId === id) closeAmenitySheet();
      return;
    }

    const template = getTemplate(id);
    if (!template) return;

    setDraftAmenity({
      template,
      displayName: template.name,
      locationMapping: 'Entire Site',
      details: {},
    });
    setConfiguringId(id);
  };

  const configuredList = useMemo(
    () =>
      [...selectedIds]
        .map(id => configured[id])
        .filter(Boolean),
    [selectedIds, configured]
  );

  const configuringAmenity = configuringId
    ? draftAmenity ?? configured[configuringId]
    : null;

  const totalFlats = totalFlatsForSite(siteData.blocks);

  const handleSubmit = async () => {
    setSubmitting(true);
    setProgress({ step: 'blocks', status: 'in_progress' });

    try {
      const result = await submitFullOnboarding(
        siteData.siteId,
        siteData,
        configuredList,
        setProgress
      );

      if (result.success) {
        setShowReview(false);
        onComplete?.(result);
      } else {
        alert(result.message);
      }
    } catch (error) {
      alert(error.message || 'Failed to save community setup.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`community-onboarding-container ${isSidebar ? 'sidebar-mode' : ''}`}>
      <div className="community-onboarding-header">
        <button type="button" onClick={onBack} className="back-button" title="Back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="community-onboarding-header-text">
          <h2 className="community-onboarding-title">Amenities</h2>
          {siteName && <p className="community-onboarding-subtitle">{siteName}</p>}
        </div>
      </div>

      <OnboardingStepper currentStep={1} />

      <div className="community-context-card">
        <MapPin className="w-5 h-5 text-teal-700" />
        <div>
          <p className="community-context-title">{siteTypeTitle(siteData.siteType)}</p>
          <p className="community-context-subtitle">
            {siteData.blocks.length} structures · {totalFlats} units
          </p>
        </div>
      </div>

      <div className="community-onboarding-scroll">
        <section className="community-section community-amenity-setup">
          <h3 className="community-section-title">
            {AMENITY_CATEGORY_META[AMENITY_CATEGORIES.CLUB_HOUSE].title}
          </h3>
          <p className="community-section-subtitle">
            Pick from common community amenities
          </p>
          <label className="community-field community-clubhouse-floors">
            <span>Club House floors</span>
            <input
              type="number"
              min={1}
              max={25}
              value={clubHouse.floors}
              onChange={e =>
                setClubHouse({
                  ...clubHouse,
                  floors: Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 1)),
                })
              }
            />
          </label>
          <div className="community-amenity-search">
            <SearchBar
              placeholder="Search amenities across all categories…"
              value={amenitySearch}
              onChange={e => setAmenitySearch(e.target.value)}
              onSuggestionClick={name => setAmenitySearch(name)}
              suggestions={amenitySearchSuggestions}
              iconType="search"
              className="community-amenity-search-bar"
            />
          </div>
        </section>

        {AMENITY_CATEGORY_IDS.map(categoryId => {
          const meta = AMENITY_CATEGORY_META[categoryId];
          const templates = filterTemplatesBySearch(templatesForCategory(categoryId));
          const isClubHouse = categoryId === AMENITY_CATEGORIES.CLUB_HOUSE;

          if (normalizedAmenitySearch && templates.length === 0) {
            return null;
          }

          return (
            <section
              key={categoryId}
              className={`community-section${isClubHouse ? ' community-section--clubhouse-chips' : ''}`}
            >
              {!isClubHouse && (
                <>
                  <h3 className="community-section-title">{meta.title}</h3>
                  <p className="community-section-subtitle">
                    Pick from common community amenities
                  </p>
                </>
              )}

              <div className="community-amenity-grid">
                {templates.map(template => (
                  <button
                    key={template.id}
                    type="button"
                    className={`community-amenity-chip ${
                      selectedIds.has(template.id) ? 'selected' : ''
                    }`}
                    onClick={() => toggleAmenity(template.id)}
                  >
                    {template.name}
                  </button>
                ))}
              </div>
            </section>
          );
        })}

        {normalizedAmenitySearch && !hasAmenitySearchResults && (
          <p className="community-amenity-search-empty">
            No amenities match &ldquo;{amenitySearch.trim()}&rdquo;
          </p>
        )}

        {configuredList.length > 0 && (
          <section className="community-section">
            <h3 className="community-section-title">Configured Amenities</h3>
            <p className="community-section-subtitle">Tap Configure to edit details</p>
            {configuredList.map(amenity => (
              <div key={amenity.template.id} className="community-configured-amenity">
                <div className="community-configured-amenity-header">
                  <div>
                    <span>{amenity.displayName}</span>
                    <p className="community-configured-amenity-location">
                      {amenity.locationMapping}
                    </p>
                  </div>
                  <div className="community-configured-amenity-actions">
                    <button
                      type="button"
                      className="community-configure-btn"
                      onClick={() => openAmenitySheet(amenity.template.id)}
                      aria-label="Configure"
                    >
                      <Settings2 className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      className="community-remove-btn"
                      onClick={() => toggleAmenity(amenity.template.id)}
                      aria-label="Remove"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </section>
        )}

        {progress && submitting && (
          <div className="community-progress">
            <p>
              {progress.step === 'blocks' ? 'Creating blocks & units…' : 'Saving amenities…'}
            </p>
            <p className="community-progress-status">{progress.status}</p>
          </div>
        )}
      </div>

      <div className="community-onboarding-footer">
        <div className="community-onboarding-footer-actions">
          <button
            type="button"
            className="community-skip-btn"
            onClick={() => setShowReview(true)}
            disabled={submitting}
          >
            Skip
          </button>
          <PrimaryButton
            className="flex-1 h-12 text-base font-semibold"
            onClick={() => setShowReview(true)}
            disabled={submitting}
          >
            Review & Generate
          </PrimaryButton>
        </div>
      </div>

      {configuringAmenity && (
        <AmenityDetailSheet
          amenity={configuringAmenity}
          locationOptions={locationOptions}
          clubHouse={clubHouse}
          onSave={updated => saveAmenityConfig(configuringId, updated)}
          onClose={closeAmenitySheet}
        />
      )}

      {showReview &&
        createPortal(
          <div
            className="community-review-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="community-review-title"
          >
            <div className="community-review-modal">
              <div className="community-review-icon" aria-hidden="true">
                <CheckCircle2 className="w-9 h-9" strokeWidth={2} />
              </div>
              <h3 id="community-review-title">Site Ready</h3>
              <p className="community-review-type">{siteTypeTitle(siteData.siteType)}</p>
              <div className="community-review-stats">
                <span>{siteData.numberOfBlocks} structures</span>
                <span className="community-review-stat-sep" aria-hidden="true" />
                <span>{totalFlats} units</span>
                <span className="community-review-stat-sep" aria-hidden="true" />
                <span>{configuredList.length} amenities</span>
              </div>
              <p className="community-review-detail">
                Club House: {clubHouse.floors} floor{clubHouse.floors === 1 ? '' : 's'}
              </p>
              {configuredList.length > 0 && (
                <ul className="community-review-list">
                  {configuredList.slice(0, 5).map(a => (
                    <li key={a.template.id}>
                      {a.displayName} → {a.locationMapping}
                    </li>
                  ))}
                  {configuredList.length > 5 && (
                    <li>+ {configuredList.length - 5} more…</li>
                  )}
                </ul>
              )}
              <div className="community-review-actions">
                <button
                  type="button"
                  className="community-review-back-btn"
                  onClick={() => setShowReview(false)}
                  disabled={submitting}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="community-review-confirm-btn"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? 'Saving…' : 'Confirm & Generate'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CommunityAmenities;
